import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { TEST_PROFILES, getProfileById } from '../views/Offers/OfferActions/TestVendorRequest/TestVendorRequestProfiles';
import {
  renderTemplate,
  resolveValue,
  getRequestDerivedValues,
} from '../utils/testVendorRequest';
import {
  useRequestFields,
  useVariableDetection,
  useRequestDerivedValues,
  useHandlerDisplay,
  useHandlerLookup,
  useParseStatusCodes,
} from './useTestVendorRequest';
import type {
  Handler,
  TestProfile,
  VendorResponse,
  VariableValues,
  FieldMapping,
  RequestInitialValues,
  HandlerLookupResult,
} from '../types/testVendorRequest.types';

interface ReduxState {
  form?: {
    offerquestion?: {
      values?: Record<string, unknown>;
    };
    offerActionDetail?: {
      values?: Record<string, unknown>;
    };
  };
  metadata?: {
    handlers?: Handler[];
  };
}

interface FormValues {
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  otherSuccessfulHttpStatus?: string;
  [key: string]: unknown;
}

interface UseTestVendorRequestModalStateOptions {
  isOpen: boolean;
  fieldMapping?: FieldMapping;
  prefix?: string;
  initialValues?: RequestInitialValues;
}

interface UseTestVendorRequestModalStateReturn {
  auth: {
    basicAuthUsername?: string;
    basicAuthPassword?: string;
  };
  request: {
    localUrl: string;
    setLocalUrl: (value: string) => void;
    localBody: string;
    setLocalBody: (value: string) => void;
    localMethod: string;
    localHeaders: [string, string, string];
    updateHeader: (index: number, value: string) => void;
    localHandler: Handler | number | string | null;
    setLocalHandler: (value: Handler | number | string | null) => void;
  };
  profile: {
    selectedProfileId: string;
    selectedProfile: TestProfile | null;
    handleProfileChange: (id: string) => void;
  };
  overrides: {
    values: Record<string, string>;
    setOverrides: (nameOrUpdater: string | Record<string, string> | ((prev: Record<string, string>) => Record<string, string>), value?: string) => void;
  };
  response: {
    lastResponse: VendorResponse | null;
    setLastResponse: React.Dispatch<React.SetStateAction<VendorResponse | null>>;
    isSending: boolean;
    setIsSending: React.Dispatch<React.SetStateAction<boolean>>;
  };
  ui: {
    isVarsOpen: boolean;
    setIsVarsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
  computed: {
    visibleNames: string[];
    handlerDisplay: string;
    handler: HandlerLookupResult;
    otherStatuses: number[];
  };
}

/**
 * Custom hook to manage TestPostModal state and logic.
 */
export const useTestVendorRequestModalState = ({
  isOpen,
  fieldMapping,
  prefix,
  initialValues
}: UseTestVendorRequestModalStateOptions): UseTestVendorRequestModalStateReturn => {
  const formValues = useSelector<ReduxState, FormValues>(
    (state) => (state.form?.offerquestion?.values || state.form?.offerActionDetail?.values || {}) as FormValues
  );
  const allHandlersFromState = useSelector<ReduxState, Handler[]>((state) => state.metadata?.handlers || []);

  const { basicAuthUsername, basicAuthPassword, otherSuccessfulHttpStatus } = formValues;

  const {
    localUrl,
    setLocalUrl,
    localBody,
    setLocalBody,
    localMethod,
    localHeaders,
    updateHeader,
    localHandler,
    setLocalHandler,
  } = useRequestFields(formValues as Record<string, string | number | Handler | null | undefined>, fieldMapping, prefix, isOpen, initialValues);

  const [selectedProfileId, setSelectedProfileId] = useState<string>(TEST_PROFILES[0]?.id ?? '');
  const selectedProfile = useMemo<TestProfile | null>(() => getProfileById(selectedProfileId), [selectedProfileId]);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const [lastResponse, setLastResponse] = useState<VendorResponse | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const [isVarsOpen, setIsVarsOpen] = useState<boolean>(true);

  const { variables } = useVariableDetection(
    localUrl,
    localBody,
    localMethod,
    selectedProfile
  );

  const EXCLUDED_VAR_NAMES = useMemo<Set<string>>(() => new Set(['body', 'url']), []);

  const literalQueryParamKeys = useMemo<string[]>(() => {
    if (localMethod !== 'GET') return [];
    const merged: VariableValues = { ...(selectedProfile || {}), ...(overrides || {}) };
    const renderedUrl = renderTemplate(localUrl || '', merged);
    try {
      const url = new URL(renderedUrl, 'http://placeholder');
      return Array.from(url.searchParams.keys()).filter((name) => /(token|key)/i.test(name));
    } catch {
      return [];
    }
  }, [localMethod, localUrl, selectedProfile, overrides]);

  const requestDerivedValues = useRequestDerivedValues(localUrl, localBody, localMethod, selectedProfile);

  const bodyKeyCandidate = useMemo<string | null>(() => {
    if (localMethod === 'GET') return null;
    const keys = Object.keys(requestDerivedValues || {});
    return keys.find((key) => key.toLowerCase() === 'key') || null;
  }, [localMethod, requestDerivedValues]);

  const visibleNames = useMemo<string[]>(() => {
    const base = new Set<string>(variables || []);
    (literalQueryParamKeys || []).forEach((literalKey) => base.add(literalKey));
    if (bodyKeyCandidate) base.add(bodyKeyCandidate);
    return Array.from(base).filter((name) => !EXCLUDED_VAR_NAMES.has(name));
  }, [variables, literalQueryParamKeys, EXCLUDED_VAR_NAMES, bodyKeyCandidate]);

  const handlerDisplay = useHandlerDisplay(localHandler);
  const handler = useHandlerLookup(allHandlersFromState, localHandler);
  const otherStatuses = useParseStatusCodes(otherSuccessfulHttpStatus);

  const resetState = useCallback((): void => {
    setOverrides({});
    setLastResponse(null);
    setTouchedFields(new Set());
    setSelectedProfileId(TEST_PROFILES[0]?.id ?? '');
    setIsVarsOpen(true);
  }, []);

  const handleProfileChange = useCallback(
    (id: string): void => {
      setSelectedProfileId(id);
      setTouchedFields(new Set());
      const profile = getProfileById(id);
      if (!profile) return;

      const mergedWithNewProfile: VariableValues = { ...(profile || {}) };
      const renderedUrl = renderTemplate(localUrl || '', mergedWithNewProfile);
      const renderedBody =
        localMethod && localMethod !== 'GET' ? renderTemplate(localBody || '', mergedWithNewProfile) : '';
      const localRequestDerived = getRequestDerivedValues(renderedUrl, renderedBody);

      const next: Record<string, string> = {};
      (visibleNames || []).forEach((key) => {
        if (key in localRequestDerived) {
          const val = localRequestDerived[key];
          next[key] = val === null ? '' : String(val);
        } else {
          const pval = resolveValue(profile as unknown as VariableValues, key);
          if (pval !== undefined && pval !== null && String(pval) !== '') {
            next[key] = String(pval);
          } else {
            next[key] = '';
          }
        }
      });

      setOverrides(next);
    },
    [localUrl, localBody, localMethod, visibleNames]
  );

  useEffect(() => {
    if (!isOpen) {
      resetState();
    } else if (Object.keys(overrides).length === 0) {
      handleProfileChange(selectedProfileId);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setOverrides((prev) => {
      const allowed = new Set(visibleNames || []);
      const next: Record<string, string> = Object.fromEntries(Object.entries(prev).filter(([key]) => allowed.has(key)));
      let changed = Object.keys(next).length !== Object.keys(prev).length;

      (visibleNames || []).forEach((key) => {
        if (touchedFields.has(key)) return;

        const hasValue = next[key] != null && next[key] !== '';
        if (!hasValue && key in requestDerivedValues) {
          next[key] = requestDerivedValues[key] === null ? '' : String(requestDerivedValues[key]);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [isOpen, visibleNames, requestDerivedValues, touchedFields]);

  const setOverrideWithTouch = useCallback((
    nameOrUpdater: string | Record<string, string> | ((prev: Record<string, string>) => Record<string, string>),
    value?: string
  ): void => {
    if (typeof nameOrUpdater === 'function') {
      setOverrides(nameOrUpdater);
    } else if (typeof nameOrUpdater === 'string') {
      setTouchedFields((prev) => new Set([...prev, nameOrUpdater]));
      setOverrides((prev) => ({ ...prev, [nameOrUpdater]: value ?? '' }));
    } else {
      const keys = Object.keys(nameOrUpdater || {});
      if (keys.length > 0) {
        setTouchedFields((prev) => new Set([...prev, ...keys]));
      }
      setOverrides(nameOrUpdater);
    }
  }, []);

  return {
    auth: { basicAuthUsername, basicAuthPassword },

    request: {
      localUrl,
      setLocalUrl,
      localBody,
      setLocalBody,
      localMethod,
      localHeaders,
      updateHeader,
      localHandler,
      setLocalHandler,
    },

    profile: { selectedProfileId, selectedProfile, handleProfileChange },

    overrides: { values: overrides, setOverrides: setOverrideWithTouch },

    response: { lastResponse, setLastResponse, isSending, setIsSending },

    ui: { isVarsOpen, setIsVarsOpen },

    computed: { visibleNames, handlerDisplay, handler, otherStatuses },
  };
};

